// Settings — admin-only sub-pages for watchlists CRUD, paging config,
// logo registry, and audit log. RBAC enforcement is on the backend
// (HTTP 403 on PUT for non-admins); this UI surfaces the gates and
// lets admins manage state without raw curl.
import React, { useEffect, useState } from 'react';
import {
    bp_watchlists_get, bp_watchlists_put, bp_watchlists_audit,
    bp_paging_get, bp_paging_put, bp_paging_test,
    bp_logo_registry_get, bp_logo_registry_add, bp_logo_registry_remove,
    bp_list_muted_domains, bp_mute_domain, bp_unmute_domain,
    bp_list_watchlist_entries, bp_create_watchlist_entry, bp_delete_watchlist_entry,
    bp_rescan_mentions, bp_scan_status, bp_anchor_preview,
    bp_get_boolean_query, bp_put_boolean_query,
    bp_put_scan_cadence, bp_serpapi_usage,
} from '../../../services';
import { unwrap } from './_shared';
import TakedownSlaWidget from '../../ExposureMap/TakedownSlaWidget';
import AIOnboardingCard from '../../BrandRisk/AIOnboardingCard';
import PrivacySection from './PrivacySection';
import RetentionSection from './RetentionSection';
import CommonButton from '../../../Components/CommonButton';
import StatusBadge from '../../../Components/StatusBadge';
import { useConfirm } from '../../../Components/ConfirmModal';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonDatePicker from '../../../Components/CommonDatePicker';
import CommonLoader from '../../../Components/CommonLoader';
import { useToast } from '../../../Components/CommonToast';
import { SubHeading, Body } from '../../../Constants/Fonts';
import './brand-mentions.scss';

const PAGING_PROVIDER_OPTIONS = [
    { value: '', label: '(off)' },
    { value: 'stub', label: 'stub (log-only)' },
    { value: 'pagerduty', label: 'pagerduty' },
    { value: 'opsgenie', label: 'opsgenie' },
    { value: 'slack', label: 'slack' },
];
const PAGING_SEVERITY_OPTIONS = [
    { value: 'Critical', label: 'Critical only' },
    { value: 'High', label: 'High and above' },
    { value: 'Medium', label: 'Medium and above' },
    { value: 'Low', label: 'Low and above' },
];
const AUDIT_FIELD_OPTIONS = [
    { value: '', label: 'All fields' },
    { value: 'facility_watchlist', label: 'Facilities' },
    { value: 'event_watchlist', label: 'Events' },
    { value: 'exec_travel_watchlist', label: 'Exec travel' },
    { value: 'vaccine_initiative_watchlist', label: 'Vaccine initiatives' },
    { value: 'monitored_topics', label: 'Monitored topics' },
    { value: 'web_feeds', label: 'Web feeds' },
];

const SECTIONS = [
    { key: 'watchlists', label: 'Watchlists' },
    { key: 'anchors', label: 'Mention anchors' },
    { key: 'privacy', label: 'Privacy & masking' },
    { key: 'retention', label: 'Data retention' },
    { key: 'paging', label: 'Paging' },
    { key: 'logos', label: 'Logo registry' },
    // { key: 'muted', label: 'Muted domains' },
    { key: 'takedowns', label: 'Takedowns' },
    { key: 'audit', label: 'Audit log' },
];


function Pill({ children, tone = 'slate' }) {
    const palette = {
        slate: { fg: '#475569', bg: 'rgba(148,163,184,0.15)', border: 'rgba(148,163,184,0.45)' },
        green: { fg: '#15803d', bg: 'rgba(34, 197, 94, 0.13)', border: 'rgba(34, 197, 94, 0.45)' },
        red: { fg: '#b91c1c', bg: 'rgba(220, 38, 38, 0.10)', border: 'rgba(220, 38, 38, 0.45)' },
        blue: { fg: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.45)' },
    };
    const p = palette[tone] || palette.slate;
    return (
        <Body as="span" variant={2} weight="semibold" style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 4,
            fontSize: 10, letterSpacing: 0.4,
            color: p.fg, background: p.bg, border: `1px solid ${p.border}`,
            marginRight: 6,
        }}>{children}</Body>
    );
}


// ─── Watchlists ──────────────────────────────────────────────────────
// Per-field structured editors. No raw JSON exposed — each entry is a
// form row with the right input shape for that watchlist's schema.
const INPUT_STYLE = {
    background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1',
    borderRadius: 4, padding: '6px 10px', fontSize: 13, width: '100%',
};
const ROW_STYLE = {
    display: 'grid', gap: 8, alignItems: 'center', marginBottom: 8,
};
const DEL_BTN = (onClick) => (
    <CommonButton onClick={onClick}
        variant="error"
        className="bm_settings_del_btn"
        title="Remove this entry"
        text="×"
    />
);

function FacilityRowEditor({ items, onChange }) {
    const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { name: '', address: '' }]);
    return (
        <>
            <Body as="div" variant={2} weight="semibold" color="#64748b" style={{
                ...ROW_STYLE, gridTemplateColumns: '1fr 2fr 40px',
                fontSize: 11,
                letterSpacing: 0.3, textTransform: 'uppercase',
                marginBottom: 4
            }}>
                <span>Name</span><span>Address</span><span></span>
            </Body>
            {items.length === 0 && (
                <Body as="div" variant={2} color="#64748b" style={{
                    fontStyle: 'italic',
                    marginBottom: 8
                }}>No facilities yet. Click "Add facility" below.</Body>
            )}
            {items.map((it, i) => (
                <div key={i} style={{ ...ROW_STYLE, gridTemplateColumns: '1fr 2fr 40px' }}>
                    <input style={INPUT_STYLE} placeholder="e.g. CEPI Oslo HQ"
                        value={it.name || ''}
                        onChange={e => update(i, { name: e.target.value })} />
                    <input style={INPUT_STYLE} placeholder="e.g. Marcus Thranes gate 2, Oslo"
                        value={it.address || ''}
                        onChange={e => update(i, { address: e.target.value })} />
                    {DEL_BTN(() => remove(i))}
                </div>
            ))}
            <CommonButton text="+ Add facility" variant="primary" className="bm_scan_btn"
                onClick={add} style={{ marginTop: 4 }}
            />
        </>
    );
}

function EventRowEditor({ items, onChange }) {
    const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { name: '', start: '', end: '', location: '' }]);
    return (
        <>
            <Body as="div" variant={2} weight="semibold" color="#64748b" style={{
                ...ROW_STYLE, gridTemplateColumns: '2fr 1fr 1fr 1fr 40px',
                fontSize: 11,
                letterSpacing: 0.3, textTransform: 'uppercase',
                marginBottom: 4
            }}>
                <span>Event name</span><span>Start</span><span>End</span><span>Location</span><span></span>
            </Body>
            {items.length === 0 && (
                <Body as="div" variant={2} color="#64748b" style={{
                    fontStyle: 'italic',
                    marginBottom: 8
                }}>No events yet.</Body>
            )}
            {items.map((it, i) => (
                <div key={i} style={{ ...ROW_STYLE, gridTemplateColumns: '2fr 1fr 1fr 1fr 40px' }}>
                    <input style={INPUT_STYLE} placeholder="e.g. World Economic Forum"
                        value={it.name || ''}
                        onChange={e => update(i, { name: e.target.value })} />
                    <CommonDatePicker
                        value={it.start || ''}
                        onChange={v => update(i, { start: v })} />
                    <CommonDatePicker
                        value={it.end || ''}
                        onChange={v => update(i, { end: v })} />
                    <input style={INPUT_STYLE} placeholder="e.g. Davos"
                        value={it.location || ''}
                        onChange={e => update(i, { location: e.target.value })} />
                    {DEL_BTN(() => remove(i))}
                </div>
            ))}
            <CommonButton text="+ Add event" variant="primary" className="bm_scan_btn"
                onClick={add} style={{ marginTop: 4 }}
            />
        </>
    );
}

function TravelRowEditor({ items, onChange }) {
    const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { exec: '', from: '', to: '', destination: '' }]);
    return (
        <>
            <Body as="div" variant={2} weight="semibold" color="#64748b" style={{
                ...ROW_STYLE, gridTemplateColumns: '1.5fr 1fr 1fr 1.5fr 40px',
                fontSize: 11,
                letterSpacing: 0.3, textTransform: 'uppercase',
                marginBottom: 4
            }}>
                <span>Executive</span><span>From</span><span>To</span><span>Destination</span><span></span>
            </Body>
            {items.length === 0 && (
                <Body as="div" variant={2} color="#64748b" style={{
                    fontStyle: 'italic',
                    marginBottom: 8
                }}>No travel windows yet.</Body>
            )}
            {items.map((it, i) => (
                <div key={i} style={{ ...ROW_STYLE, gridTemplateColumns: '1.5fr 1fr 1fr 1.5fr 40px' }}>
                    <input style={INPUT_STYLE} placeholder="e.g. Richard Hatchett"
                        value={it.exec || ''}
                        onChange={e => update(i, { exec: e.target.value })} />
                    <CommonDatePicker
                        value={it.from || ''}
                        onChange={v => update(i, { from: v })} />
                    <CommonDatePicker
                        value={it.to || ''}
                        onChange={v => update(i, { to: v })} />
                    <input style={INPUT_STYLE} placeholder="e.g. Davos"
                        value={it.destination || ''}
                        onChange={e => update(i, { destination: e.target.value })} />
                    {DEL_BTN(() => remove(i))}
                </div>
            ))}
            <CommonButton text="+ Add travel window" variant="primary" className="bm_scan_btn"
                onClick={add} style={{ marginTop: 4 }}
            />
        </>
    );
}

// Each row owns its own raw-text state for the comma-separated fields.
// We keep the displayed value as-typed (commas, trailing spaces, repeated
// delimiters — everything the user is mid-typing) and only emit the
// parsed array upward. The previous implementation parsed on every
// keystroke and re-rendered .join(', '), which silently erased trailing
// commas/spaces and made it impossible to type a second keyword.
function TopicRow({ it, onUpdate, onRemove }) {
    const initialKw = Array.isArray(it.keywords) ? it.keywords.join(', ') : '';
    const initialExc = Array.isArray(it.exclude) ? it.exclude.join(', ') : '';
    const [kwText, setKwText] = useState(initialKw);
    const [excText, setExcText] = useState(initialExc);
    const toList = (raw) => raw.split(',').map(s => s.trim()).filter(Boolean);
    return (
        <div style={{ ...ROW_STYLE, gridTemplateColumns: '2fr 3fr 3fr 40px' }}>
            <input style={INPUT_STYLE} placeholder="e.g. Disease X"
                value={it.name || ''}
                onChange={e => onUpdate({ name: e.target.value })} />
            <input style={INPUT_STYLE}
                placeholder="disease x, pandemic preparedness, CEPI 3.0"
                value={kwText}
                onChange={e => {
                    setKwText(e.target.value);
                    onUpdate({ keywords: toList(e.target.value) });
                }} />
            <input style={INPUT_STYLE}
                placeholder="(optional) phrases to drop"
                value={excText}
                onChange={e => {
                    setExcText(e.target.value);
                    onUpdate({ exclude: toList(e.target.value) });
                }} />
            {DEL_BTN(onRemove)}
        </div>
    );
}

function MonitoredTopicsEditor({ items, onChange }) {
    const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { name: '', keywords: [], exclude: [] }]);
    return (
        <>
            <Body as="div" variant={2} weight="semibold" color="#64748b" style={{
                ...ROW_STYLE, gridTemplateColumns: '2fr 3fr 3fr 40px',
                fontSize: 11,
                letterSpacing: 0.3, textTransform: 'uppercase',
                marginBottom: 4
            }}>
                <span>Topic name</span><span>Keywords (comma-sep)</span><span>Exclude (optional)</span><span></span>
            </Body>
            {items.length === 0 && (
                <Body as="div" variant={2} color="#64748b" style={{
                    fontStyle: 'italic',
                    marginBottom: 8
                }}>
                    No custom topics yet. Add a topic to track its mentions, reach,
                    sentiment, and share of voice in the Topic Analysis tab.
                </Body>
            )}
            {items.map((it, i) => (
                <TopicRow key={i} it={it}
                    onUpdate={patch => update(i, patch)}
                    onRemove={() => remove(i)} />
            ))}
            <CommonButton text="+ Add topic" variant="primary" className="bm_scan_btn"
                onClick={add} style={{ marginTop: 4 }}
            />
        </>
    );
}

function WebFeedRowEditor({ items, onChange }) {
    const update = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, { url: '', label: '' }]);
    return (
        <>
            <Body as="div" variant={2} weight="semibold" color="#64748b" style={{
                ...ROW_STYLE, gridTemplateColumns: '2fr 1fr 40px',
                fontSize: 11,
                letterSpacing: 0.3, textTransform: 'uppercase',
                marginBottom: 4
            }}>
                <span>Feed URL (RSS / Atom)</span><span>Label</span><span></span>
            </Body>
            {items.length === 0 && (
                <Body as="div" variant={2} color="#64748b" style={{
                    fontStyle: 'italic',
                    marginBottom: 8
                }}>
                    No feeds yet. Add an RSS/Atom URL like
                    {' '}<code>https://blog.example.com/feed/</code> below.
                </Body>
            )}
            {items.map((it, i) => (
                <div key={i} style={{ ...ROW_STYLE, gridTemplateColumns: '2fr 1fr 40px' }}>
                    <input style={INPUT_STYLE} placeholder="https://blog.example.com/feed/"
                        value={it.url || ''}
                        onChange={e => update(i, { url: e.target.value })} />
                    <input style={INPUT_STYLE} placeholder="e.g. WHO Disease News"
                        value={it.label || ''}
                        onChange={e => update(i, { label: e.target.value })} />
                    {DEL_BTN(() => remove(i))}
                </div>
            ))}
            <CommonButton text="+ Add feed" variant="primary" className="bm_scan_btn"
                onClick={add} style={{ marginTop: 4 }}
            />
        </>
    );
}

function StringListEditor({ items, onChange, placeholder }) {
    const update = (i, v) => onChange(items.map((it, idx) => idx === i ? v : it));
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, '']);
    return (
        <>
            {items.length === 0 && (
                <Body as="div" variant={2} color="#64748b" style={{
                    fontStyle: 'italic',
                    marginBottom: 8
                }}>No entries yet.</Body>
            )}
            {items.map((it, i) => (
                <div key={i} style={{ ...ROW_STYLE, gridTemplateColumns: '1fr 40px' }}>
                    <input style={INPUT_STYLE} placeholder={placeholder}
                        value={typeof it === 'string' ? it : ''}
                        onChange={e => update(i, e.target.value)} />
                    {DEL_BTN(() => remove(i))}
                </div>
            ))}
            <CommonButton text="+ Add entry" variant="primary" className="bm_scan_btn"
                onClick={add} style={{ marginTop: 4 }}
            />
        </>
    );
}

function VaccineInitiativeEditor({ items, onChange }) {
    return <StringListEditor items={items} onChange={onChange} placeholder="e.g. 100 Days Mission" />;
}

function WatchlistsSection() {
    const [data, setData] = useState({
        facility_watchlist: [], event_watchlist: [],
        exec_travel_watchlist: [], vaccine_initiative_watchlist: [],
        monitored_topics: [],
        web_feeds: [],
    });
    const [draft, setDraft] = useState(null);  // { field, items } when editing
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const { notify, toastNode } = useToast();

    const load = () => {
        bp_watchlists_get().then(r => setData(unwrap(r) || {})).catch(() => { });
    };
    useEffect(() => { load(); }, []);

    const beginEdit = (field) => {
        setErr(null);
        setDraft({ field, items: JSON.parse(JSON.stringify(data[field] || [])) });
    };

    const cancelEdit = () => { setDraft(null); setErr(null); };

    const save = async () => {
        if (!draft) return;
        setSaving(true); setErr(null);
        // Drop fully-empty rows so empty inputs aren't persisted.
        const cleaned = (draft.items || []).filter(it => {
            if (typeof it === 'string') return it.trim().length > 0;
            return Object.values(it || {}).some(v => (v || '').toString().trim().length > 0);
        });
        const res = await bp_watchlists_put({ [draft.field]: cleaned });
        const body = res?.statusCode || {};
        if (body.status !== 1) {
            setErr(body.detail || body.error || 'Save failed.');
        } else {
            notify(`Saved ${cleaned.length} entries to ${LABELS[draft.field]}.`, 'success');
            setDraft(null);
            load();
        }
        setSaving(false);
    };

    const FIELDS = [
        {
            key: 'facility_watchlist', label: 'Facilities',
            help: 'Facility names or addresses to monitor for protest/activist chatter.',
            Editor: FacilityRowEditor,
            renderItem: (it) => `${it.name}${it.address ? ' — ' + it.address : ''}`
        },
        {
            key: 'event_watchlist', label: 'Events',
            help: 'Events with date windows. Mentions referencing the event inside its window get flagged.',
            Editor: EventRowEditor,
            renderItem: (it) => `${it.name}${it.start ? ` (${it.start} → ${it.end || it.start})` : ''}${it.location ? ' · ' + it.location : ''}`
        },
        {
            key: 'exec_travel_watchlist', label: 'Exec travel',
            help: 'Exec travel windows. Mentions referencing the exec inside the window get flagged as travel-targeting.',
            Editor: TravelRowEditor,
            renderItem: (it) => `${it.exec}${it.destination ? ' → ' + it.destination : ''}${it.from ? ` (${it.from} → ${it.to || it.from})` : ''}`
        },
        {
            key: 'vaccine_initiative_watchlist', label: 'Vaccine initiatives',
            help: 'Initiative names whose impersonation should be flagged.',
            Editor: VaccineInitiativeEditor,
            renderItem: (it) => (typeof it === 'string' ? it : '')
        },
        {
            key: 'monitored_topics', label: 'Monitored topics',
            help: 'Custom topics to surface as their own cards in Topic Analysis. ' +
                'Each topic groups mentions whose body matches any of its keywords. ' +
                'Use "exclude" to suppress false positives.',
            Editor: MonitoredTopicsEditor,
            renderItem: (it) => `${it.name}` +
                (it.keywords?.length ? ` — ${it.keywords.slice(0, 4).join(', ')}` +
                    (it.keywords.length > 4 ? ` +${it.keywords.length - 4} more` : '') : '')
        },
        // Slice 3-C — generic web/blog crawler. Each entry is an
        // RSS/Atom feed URL; the scanner fetches items matching the org's
        // brand keywords and lands them as source_platform='blog'.
        {
            key: 'web_feeds', label: 'Web feeds (RSS / Atom)',
            help: 'Blogs and news sites the scanner should poll. Drop in '
                + 'the feed URL (most blogs auto-discover at /feed/ or '
                + '/rss/). Items matching your brand keywords land in '
                + 'Mentions with source_platform="blog".',
            Editor: WebFeedRowEditor,
            renderItem: (it) => `${it.label || '(unnamed)'} — ${it.url || '(no url)'}`
        },
    ];
    const LABELS = Object.fromEntries(FIELDS.map(f => [f.key, f.label]));

    return (
        <>
            {err && <div className="bm_empty" style={{
                color: '#b91c1c', borderColor: 'rgba(220,38,38,0.4)',
                background: 'rgba(220,38,38,0.05)'
            }}>{err}</div>}
            {toastNode}
            {FIELDS.map(({ key, label, help, Editor, renderItem }) => {
                const items = data[key] || [];
                const isEditing = draft && draft.field === key;
                return (
                    <div key={key} className="bm_topic_card cmn_elevated_card" style={{ marginBottom: 12 }}>
                        <div className="bm_topic_head" style={{ alignItems: 'center' }}>
                            <span className="bm_topic_title">{label}</span>
                            <Pill tone="blue">{items.length} {items.length === 1 ? 'entry' : 'entries'}</Pill>
                            {!isEditing ? (
                                <CommonButton text="Edit" variant="primary" className="bm_scan_btn"
                                    onClick={() => beginEdit(key)}
                                    style={{ marginLeft: 'auto' }}
                                />
                            ) : (
                                <>
                                    <CommonButton
                                        text={saving ? 'Saving…' : 'Save changes'}
                                        variant="primary" className="bm_scan_btn"
                                        onClick={save} disable={saving}
                                        style={{ marginLeft: 'auto' }}
                                    />
                                    <CommonButton text="Cancel" variant="white" className="bm_scan_btn"
                                        onClick={cancelEdit} disable={saving}
                                        style={{ marginLeft: 4 }}
                                    />
                                </>
                            )}
                        </div>
                        <p className="bm_topic_desc">{help}</p>
                        {!isEditing ? (
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: '#334155' }}>
                                {items.length === 0 && (
                                    <li style={{ color: '#64748b', fontStyle: 'italic' }}>
                                        Empty — click Edit to add entries.
                                    </li>
                                )}
                                {items.slice(0, 12).map((it, i) => (
                                    <li key={i} style={{ padding: '2px 0' }}>{renderItem(it)}</li>
                                ))}
                                {items.length > 12 && (
                                    <li style={{ color: '#64748b' }}>… and {items.length - 12} more (open Edit to see all)</li>
                                )}
                            </ul>
                        ) : (
                            <div style={{ marginTop: 8 }}>
                                <Editor items={draft.items}
                                    onChange={items => setDraft({ ...draft, items })} />
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
}


// ─── Paging ──────────────────────────────────────────────────────────
function PagingSection() {
    const [cfg, setCfg] = useState({ paging_provider: '', paging_webhook_url_set: false, paging_severity_threshold: 'High' });
    const [draft, setDraft] = useState({ paging_provider: '', paging_webhook_url: '', paging_severity_threshold: 'High' });
    const [testResult, setTestResult] = useState(null);
    const [err, setErr] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    const load = () => {
        bp_paging_get().then(r => {
            const d = unwrap(r) || {};
            setCfg(d);
            setDraft({
                paging_provider: d.paging_provider || '',
                paging_webhook_url: '',  // we never echo it back
                paging_severity_threshold: d.paging_severity_threshold || 'High',
            });
        }).catch(() => { });
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        setErr(null);
        const body = {
            paging_provider: draft.paging_provider,
            paging_severity_threshold: draft.paging_severity_threshold,
        };
        if (draft.paging_webhook_url) body.paging_webhook_url = draft.paging_webhook_url;
        const res = await bp_paging_put(body);
        const r = res?.statusCode || {};
        if (r.status !== 1) setErr(r.detail || r.error || 'Save failed.');
        else load();
        setSaving(false);
    };

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        const res = await bp_paging_test({ severity: 'High' });
        setTestResult((res?.statusCode || {}).data || res?.statusCode || res);
        setTesting(false);
    };

    return (
        <>
            {err && <div className="bm_empty" style={{ color: '#b91c1c', borderColor: 'rgba(220,38,38,0.4)' }}>{err}</div>}
            <div className="bm_topic_card cmn_elevated_card">
                <div className="bm_topic_head">
                    <span className="bm_topic_title">SLA paging</span>
                    <StatusBadge
                        variant={cfg.paging_provider ? 'success' : 'neutral'}
                        transform="none"
                        style={{ marginRight: 6 }}
                    >
                        provider: {cfg.paging_provider || '(off)'}
                    </StatusBadge>
                    <StatusBadge
                        variant={cfg.paging_webhook_url_set ? 'success' : 'danger'}
                        transform="none"
                        style={{ marginRight: 6 }}
                    >
                        {cfg.paging_webhook_url_set ? 'webhook set' : 'no webhook'}
                    </StatusBadge>
                    <StatusBadge variant="neutral" transform="none" style={{ marginRight: 6 }}>
                        threshold: {cfg.paging_severity_threshold}
                    </StatusBadge>
                </div>
                <p className="bm_topic_desc">
                    Configure the on-call dispatcher. High / Critical findings auto-fire
                    a page via the configured provider. Pick <strong>stub</strong> for
                    dev (log-only). Webhook field is write-only — current value is never
                    echoed back for security.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 8 }}>
                    <CommonDropdown
                        ariaLabel="Paging provider"
                        value={draft.paging_provider}
                        onChange={(v) => setDraft({ ...draft, paging_provider: v })}
                        options={PAGING_PROVIDER_OPTIONS}
                    />
                    <input type="password" placeholder="Webhook URL / routing key (leave blank to keep current)"
                        value={draft.paging_webhook_url}
                        onChange={e => setDraft({ ...draft, paging_webhook_url: e.target.value })}
                        style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px' }} />
                    <CommonDropdown
                        ariaLabel="Paging severity threshold"
                        value={draft.paging_severity_threshold}
                        onChange={(v) => setDraft({ ...draft, paging_severity_threshold: v })}
                        options={PAGING_SEVERITY_OPTIONS}
                    />
                </div>
                <div style={{ marginTop: 12 }}>
                    <CommonButton
                        text={saving ? 'Saving…' : 'Save paging config'}
                        variant="primary" className="bm_scan_btn"
                        onClick={save} disable={saving}
                    />
                    <CommonButton
                        text={testing ? 'Sending…' : 'Send test page'}
                        variant="white" className="bm_scan_btn"
                        onClick={runTest} disable={testing}
                        style={{ marginLeft: 6 }}
                    />
                </div>
                {testResult && (
                    <pre style={{
                        marginTop: 10, fontSize: 11, padding: 8, borderRadius: 4,
                        background: '#f8fafc', color: '#0f172a', border: '1px solid #e2e8f0',
                    }}>{JSON.stringify(testResult, null, 2)}</pre>
                )}
            </div>
        </>
    );
}


// ─── Logo registry ───────────────────────────────────────────────────
function LogoRegistrySection() {
    const [logos, setLogos] = useState([]);
    const [draftLabel, setDraftLabel] = useState('');
    const [draftSource, setDraftSource] = useState('');  // hex or URL
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    const load = () => {
        bp_logo_registry_get().then(r => {
            const d = unwrap(r) || {};
            setLogos(d.logos || []);
        }).catch(() => { });
    };
    useEffect(() => { load(); }, []);

    const add = async () => {
        setSaving(true); setErr(null);
        const body = { label: draftLabel || 'logo' };
        const trimmed = draftSource.trim();
        if (/^[0-9a-fA-F]{16}$/.test(trimmed)) body.hash = trimmed;
        else if (/^https?:\/\//.test(trimmed)) body.image_url = trimmed;
        else { setErr('Source must be a 16-char hex pHash or an http(s) image URL.'); setSaving(false); return; }
        const res = await bp_logo_registry_add(body);
        const r = res?.statusCode || {};
        if (r.status !== 1) setErr(r.detail || r.error || 'Add failed.');
        else { setDraftLabel(''); setDraftSource(''); load(); }
        setSaving(false);
    };

    const remove = async (entry) => {
        await bp_logo_registry_remove({ hash: entry.hash });
        load();
    };

    return (
        <>
            {err && <div className="bm_empty" style={{ color: '#b91c1c', borderColor: 'rgba(220,38,38,0.4)' }}>{err}</div>}
            <div className="bm_topic_card cmn_elevated_card">
                <div className="bm_topic_head">
                    <span className="bm_topic_title">Verified logos</span>
                    <Pill tone="blue">{logos.length} registered</Pill>
                </div>
                <p className="bm_topic_desc">
                    Each entry is a perceptual hash (pHash) of an approved logo.
                    The image-similarity scanner flags any mention's image whose
                    pHash falls within tolerance of these. We never store the raw
                    image — just the 64-bit hash.
                </p>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ color: '#64748b', textAlign: 'left' }}>
                            <th style={{ padding: 4 }}>Label</th>
                            <th style={{ padding: 4, fontFamily: 'monospace' }}>pHash</th>
                            <th style={{ padding: 4, width: 80 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {logos.length === 0 && <tr><td colSpan={3} style={{ color: '#64748b', padding: 8 }}>(empty — add a verified logo below)</td></tr>}
                        {logos.map((e, i) => (
                            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                                <td style={{ padding: 6, color: '#0f172a' }}>{e.label}</td>
                                <td style={{ padding: 6, fontFamily: 'monospace', color: '#475569' }}>{e.hash}</td>
                                <td style={{ padding: 6 }}>
                                    <CommonButton text="Remove" variant="error" className="bm_settings_del_btn"
                                        onClick={() => remove(e)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 2fr 80px', gap: 8 }}>
                    <input placeholder="Label (e.g. CEPI primary logo)" value={draftLabel}
                        onChange={e => setDraftLabel(e.target.value)}
                        style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px' }} />
                    <input placeholder="16-char hex pHash or https://… image URL" value={draftSource}
                        onChange={e => setDraftSource(e.target.value)}
                        style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px' }} />
                    <CommonButton
                        text={saving ? 'Adding…' : 'Add'}
                        variant="primary" className="bm_scan_btn"
                        onClick={add} disable={saving}
                    />
                </div>
            </div>
        </>
    );
}


// ─── Takedowns (SLA overview) ────────────────────────────────────────
// Relocated here from the former /brand-protection/watchlist page. The
// widget self-hides when the org has no takedowns; the explainer always
// shows so the surface is discoverable.
function TakedownsSection() {
    return (
        <div className="bm_topic_card cmn_elevated_card">
            <div className="bm_topic_head">
                <span className="bm_topic_title">Takedown SLA</span>
            </div>
            <p className="bm_topic_desc">
                SLA tracking for takedown requests — initiate ≤ 24h, resolve ≤ 5 days.
                Takedowns are filed from the <strong>Mentions</strong> tab (the
                "Request takedown" action on a mention) and from confirmed
                <strong> Threat Monitor</strong> findings; this panel tracks how fast
                they're filed and resolved against SLA.
            </p>
            <TakedownSlaWidget />
        </div>
    );
}


// ─── Audit log ───────────────────────────────────────────────────────
function AuditLogSection() {
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [field, setField] = useState('');

    useEffect(() => {
        const params = field ? { field, limit: 100 } : { limit: 100 };
        bp_watchlists_audit(params).then(r => {
            const d = unwrap(r) || {};
            setRows(d.entries || []);
            setCount(d.count || 0);
        }).catch(() => { });
    }, [field]);

    return (
        <div className="bm_topic_card cmn_elevated_card">
            <div className="bm_topic_head">
                <span className="bm_topic_title">Watchlist audit log</span>
                <Pill tone="blue">{count} entries total</Pill>
                <CommonDropdown
                    ariaLabel="Audit field filter"
                    value={field}
                    onChange={setField}
                    options={AUDIT_FIELD_OPTIONS}
                    style={{ marginLeft: 'auto' }}
                />
            </div>
            <p className="bm_topic_desc">
                Append-only audit trail. Every watchlist edit is recorded with the
                user, before/after counts, and timestamp.
            </p>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ color: '#64748b', textAlign: 'left' }}>
                        <th style={{ padding: 4 }}>When</th>
                        <th style={{ padding: 4 }}>User</th>
                        <th style={{ padding: 4 }}>Field</th>
                        <th style={{ padding: 4 }}>Action</th>
                        <th style={{ padding: 4 }}>Before → After</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && <tr><td colSpan={5} style={{ color: '#64748b', padding: 8 }}>(no entries)</td></tr>}
                    {rows.map(r => (
                        <tr key={r.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                            <td style={{ padding: 6, color: '#475569', whiteSpace: 'nowrap' }}>{r.created_at.slice(0, 19).replace('T', ' ')}</td>
                            <td style={{ padding: 6, color: '#0f172a' }}>{r.user_email || '(system)'}</td>
                            <td style={{ padding: 6, color: '#475569' }}>{r.field}</td>
                            <td style={{ padding: 6 }}><StatusBadge status={r.action} variant="neutral">{r.action}</StatusBadge></td>
                            <td style={{ padding: 6, color: '#475569' }}>{(r.before || []).length} → {(r.after || []).length}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


function MutedDomainsSection() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({ domain: '', reason: '' });
    const [busy, setBusy] = useState(false);

    const refresh = () => {
        setLoading(true);
        bp_list_muted_domains()
            .then(r => setRows((unwrap(r) || {}).items || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    };

    useEffect(refresh, []);

    const mute = async () => {
        const domain = (draft.domain || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        if (!domain) return;
        setBusy(true);
        try {
            await bp_mute_domain({ domain, reason: draft.reason });
            setDraft({ domain: '', reason: '' });
            refresh();
        } finally { setBusy(false); }
    };

    const unmute = async (id) => {
        setBusy(true);
        try { await bp_unmute_domain(id); refresh(); }
        finally { setBusy(false); }
    };

    return (
        <div className="cmn_elevated_card">
            <div className="bm_subtitle" style={{ marginBottom: 8 }}>
                Muted domains · {rows.length}
            </div>
            <Body variant={2} color="#94a3b8" style={{ marginTop: 0 }}>
                Domains on this list are excluded from every Brand Protection
                feed for this org until you unmute them. Use this for noisy
                domains that the scanner consistently surfaces but you don’t
                want to see (parked SEO sites, competitor blogs republishing
                scraped content, etc.).
            </Body>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                <input type="text" placeholder="example.com"
                    value={draft.domain}
                    onChange={e => setDraft(d => ({ ...d, domain: e.target.value }))}
                    style={{
                        flex: '0 0 220px', padding: '6px 10px',
                        border: '1px solid #cbd5e1', borderRadius: 4,
                        fontSize: 13
                    }} />
                <input type="text" placeholder="Reason (optional)"
                    value={draft.reason}
                    onChange={e => setDraft(d => ({ ...d, reason: e.target.value }))}
                    style={{
                        flex: 1, padding: '6px 10px',
                        border: '1px solid #cbd5e1', borderRadius: 4,
                        fontSize: 13
                    }} />
                <CommonButton text="Mute" variant="primary" className="bm_scan_btn"
                    onClick={mute} disable={busy || !draft.domain.trim()}
                />
            </div>

            {loading && <CommonLoader label="Loading…" />}
            {!loading && rows.length === 0 && (
                <Body as="div" variant={1} color="#94a3b8" style={{ padding: '12px 0' }}>
                    No domains muted. Mute a domain from the Mentions tab card
                    toolbar, or add one above.
                </Body>
            )}
            {!loading && rows.length > 0 && (
                <table className="bm_inf_table" style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '30%' }}>Domain</th>
                            <th>Reason</th>
                            <th style={{ width: 140 }}>Muted at</th>
                            <th style={{ width: 100 }}>By</th>
                            <th style={{ width: 80 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.id}>
                                <td style={{ fontFamily: 'ui-monospace, monospace' }}>{r.domain}</td>
                                <td style={{ color: '#475569' }}>{r.reason || '—'}</td>
                                <td style={{ fontSize: 11, color: '#64748b' }}>
                                    {r.muted_at ? new Date(r.muted_at).toLocaleString() : '—'}
                                </td>
                                <td style={{ fontSize: 11, color: '#64748b' }}>
                                    {r.muted_by || '—'}
                                </td>
                                <td>
                                    <CommonButton text="Unmute" variant="white" className="bm_scan_btn"
                                        onClick={() => unmute(r.id)} disable={busy}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ------------------------------------------------------------------
// Mention Anchors section (2026-05-26)
//
// Customer-editable CRUD for the BrandWatchlistEntry rows that drive
// `brand_mention_scan`'s precision filter. Before this section existed
// these rows were seeded server-side (migrations, ad-hoc shell), which
// meant a 4-letter org name like CEPI couldn't escape its low-
// specificity gate without engineering ticket — exactly the gap that
// kept its 14 carefully-curated claim phrases inert until 2026-05-26.
//
// Each of the seven entry types renders a typed form. The form fields
// follow the model docstring (executive→{name, role?}, programme→
// {name, aliases?}, etc.). All adds flow through one POST and the
// backend dispatches a rescan; the toast banner shows the status until
// the next page action so the customer knows their change is live.
// ------------------------------------------------------------------
const ENTRY_TYPES = [
    {
        key: 'claim_phrase',
        label: 'Claim seed phrases',
        hint: 'Exact phrases that, when present in a post body, are strong evidence the post is about your org. Examples for CEPI: "in partnership with CEPI", "CEPI-funded", "funded by CEPI". Multilingual variants welcome.',
        fields: [
            { name: 'phrase', label: 'Phrase', placeholder: 'e.g. funded by CEPI', required: true, primary: true },
        ],
        primaryField: 'phrase',
        renderRow: v => v?.phrase || '',
    },
    {
        key: 'negative_keyword',
        label: 'Negative keywords',
        hint: 'Words that, if present, DROP the mention. Used to suppress false positives — common-word brand names like "Colt" can use this to filter out "Indianapolis Colts", "Colt 45 beer", etc.',
        fields: [
            { name: 'phrase', label: 'Phrase / token', placeholder: 'e.g. Indianapolis Colts', required: true, primary: true },
        ],
        primaryField: 'phrase',
        renderRow: v => v?.phrase || '',
    },
    {
        key: 'executive',
        label: 'Executives',
        hint: 'Senior staff whose last name acts as a contextual secondary anchor. Surface CEOs, board chairs, and chief officers — anyone whose name appears in news coverage about your org.',
        fields: [
            { name: 'name', label: 'Full name', placeholder: 'e.g. Dr Richard Hatchett', required: true, primary: true },
            { name: 'role', label: 'Role (optional)', placeholder: 'Chief Executive Officer', required: false },
        ],
        primaryField: 'name',
        renderRow: v => v?.name + (v?.role ? ` · ${v.role}` : ''),
    },
    {
        key: 'programme',
        label: 'Programmes / initiatives',
        hint: 'Named programmes, products, or initiatives your org runs. Aliases let one row catch every spelling. Example: "100 Days Mission" with aliases ["100 days mission for vaccines", "100DM"].',
        fields: [
            { name: 'name', label: 'Programme name', placeholder: 'e.g. 100 Days Mission', required: true, primary: true },
            { name: 'aliases', label: 'Aliases (comma-separated)', placeholder: '100DM, 100 days mission for vaccines', required: false, parse: s => s.split(',').map(x => x.trim()).filter(Boolean) },
        ],
        primaryField: 'name',
        renderRow: v => v?.name + ((v?.aliases || []).length ? ` (also: ${(v.aliases || []).join(', ')})` : ''),
    },
    {
        key: 'partner_domain',
        label: 'Approved partner domains',
        hint: 'Domains of partners, donors, regulators. Posts that reference these domains are treated as in-context. Example: who.int, gavi.org.',
        fields: [
            { name: 'domain', label: 'Domain', placeholder: 'e.g. who.int', required: true, primary: true },
            { name: 'partner_name', label: 'Partner name (optional)', placeholder: 'World Health Organization', required: false },
            { name: 'claim_authorized', label: 'Partner is authorised to claim affiliation', type: 'checkbox', required: false },
        ],
        primaryField: 'domain',
        renderRow: v => v?.domain + (v?.partner_name ? ` · ${v.partner_name}` : ''),
    },
    {
        key: 'brand_handle',
        label: 'Brand handles',
        hint: 'Official social handles of your org. Mentions from / about these handles are treated as primary anchors.',
        fields: [
            { name: 'canonical_handle', label: 'Handle', placeholder: 'CEPIvaccines (no @ prefix)', required: true, primary: true },
            { name: 'platform', label: 'Platform', type: 'select', options: ['x', 'twitter', 'linkedin', 'facebook', 'instagram', 'bluesky', 'tiktok', 'youtube', 'reddit', 'telegram'], required: false },
        ],
        primaryField: 'canonical_handle',
        renderRow: v => {
            const handle = (v?.canonical_handle || '').replace(/^@+/, '');
            const platforms = Array.isArray(v?.platform) ? v.platform : (v?.platform ? [v.platform] : []);
            return `@${handle}` + (platforms.length ? ` on ${platforms.join(', ')}` : '');
        },
    },
    {
        key: 'event',
        label: 'Upcoming events',
        hint: 'Conferences / summits attended by your execs. Posts mentioning the event name during/after the date window become high-relevance.',
        fields: [
            { name: 'name', label: 'Event name', placeholder: 'World Health Summit', required: true, primary: true },
            { name: 'venue', label: 'Venue (optional)', placeholder: 'Berlin', required: false },
            { name: 'start_date', label: 'Start date', type: 'date', required: false },
            { name: 'end_date', label: 'End date', type: 'date', required: false },
        ],
        primaryField: 'name',
        renderRow: v => v?.name + (v?.venue ? ` · ${v.venue}` : '') +
            (v?.start_date ? ` · ${v.start_date}${v?.end_date ? '→' + v.end_date : ''}` : ''),
    },
];

function MentionAnchorsSection() {
    const { confirm, confirmModal } = useConfirm();
    const [rowsByType, setRowsByType] = useState({});
    console.log("qq", rowsByType);

    const [loading, setLoading] = useState(true);
    const [aiOpen, setAiOpen] = useState(false);   // force-show the FORESEE AI suggester
    const [drafts, setDrafts] = useState({});       // {entry_type: {field: value}}
    const [savingType, setSavingType] = useState(''); // which form is mid-save
    const [banner, setBanner] = useState(null);      // {tone, text}
    const [rescanBusy, setRescanBusy] = useState(false);
    // Pre-save preview — debounced per typeKey. Stores {count, hint,
    // is_specific_enough, loading} keyed by entry_type.
    const [previews, setPreviews] = useState({});
    // Boolean-query (textarea) — raw expression + saved summary.
    const [boolDraft, setBoolDraft] = useState('');
    const [boolSaved, setBoolSaved] = useState(null);   // {raw, clause_count, matches_in_last_30d}
    const [boolBusy, setBoolBusy] = useState(false);
    const [boolError, setBoolError] = useState('');
    // Tier selector + SerpAPI usage (cadence-control row, 2026-05-27).
    const [cadenceMinutes, setCadenceMinutes] = useState(null);   // null = loading
    const [cadenceBusy, setCadenceBusy] = useState(false);
    const [serpUsage, setSerpUsage] = useState(null);

    const triggerRescan = async () => {
        setRescanBusy(true);
        setBanner(null);
        try {
            const res = await bp_rescan_mentions();
            const d = unwrap(res) || {};
            setBanner({
                tone: 'green',
                text: d.dispatched
                    ? `Re-scan dispatched (task ${(d.task_id || '').slice(0, 8)}…) — mentions will refresh in a few minutes.`
                    : 'Re-scan acknowledged but not dispatched — check server logs.',
            });
        } catch (err) {
            const msg = err?.response?.data?.message.split('Manual scan rate-limited')[1] || err?.message || 'dispatch failed';
            console.log("ll", msg);

            setBanner({ tone: 'red', text: `Could not start scan: ${msg}` });
        } finally {
            setRescanBusy(false);
        }
    };

    const refresh = async () => {
        setLoading(true);
        try {
            const res = await bp_list_watchlist_entries({});
            const data = unwrap(res) || {};
            const entries = data.entries || [];
            const byType = {};
            for (const e of entries) {
                (byType[e.entry_type] = byType[e.entry_type] || []).push(e);
            }
            setRowsByType(byType);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    useEffect(() => {
        bp_get_boolean_query()
            .then(r => {
                const d = unwrap(r) || {};
                setBoolSaved(d);
                setBoolDraft(d.raw || '');
            })
            .catch(() => { });
        // Load current cadence. Backend's scan-status response is inconsistent
        // about whether it echoes the manual_only flag, so we layer two reads:
        //  1. localStorage (last value the user picked in this browser)
        //  2. server response — manual_only=true OR cadence_minutes=0 → "Manual"
        // The localStorage fallback wins when the server omits manual_only.
        const orgId = localStorage.getItem('organization_id') || '0';
        const lsKey = `bp_scan_cadence_org_${orgId}`;
        const lsCached = localStorage.getItem(lsKey);
        bp_scan_status()
            .then(r => {
                const d = unwrap(r) || {};
                if (d.manual_only === true || d.cadence_minutes === 0) {
                    setCadenceMinutes(0);
                } else if (typeof d.cadence_minutes === 'number') {
                    setCadenceMinutes(d.cadence_minutes);
                } else if (lsCached !== null) {
                    setCadenceMinutes(parseInt(lsCached, 10));
                } else {
                    setCadenceMinutes(120);
                }
            })
            .catch(() => {
                if (lsCached !== null) setCadenceMinutes(parseInt(lsCached, 10));
                else setCadenceMinutes(120);
            });
        bp_serpapi_usage()
            .then(r => setSerpUsage(unwrap(r) || null))
            .catch(() => { });
    }, []);

    const handleCadenceChange = async (newValue) => {
        const cadence = parseInt(newValue, 10);
        setCadenceBusy(true);
        // Optimistic: reflect the user's pick immediately and persist it to
        // localStorage so a tab switch can't silently revert the dropdown
        // even if the backend response omits manual_only / cadence_minutes.
        setCadenceMinutes(cadence);
        try {
            const orgId = localStorage.getItem('organization_id') || '0';
            localStorage.setItem(`bp_scan_cadence_org_${orgId}`, String(cadence));
        } catch (_) { /* ignore quota */ }
        try {
            const res = await bp_put_scan_cadence({ cadence_minutes: cadence });
            const d = unwrap(res) || {};
            setBanner({
                tone: 'green',
                text: (d.manual_only || cadence === 0)
                    ? 'Scheduled scans turned off · this org is now manual-only'
                    : `Scheduled cadence updated to every ${d.cadence_hours ?? (cadence / 60)} hour${(d.cadence_hours ?? (cadence / 60)) === 1 ? '' : 's'}`,
            });
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'save failed';
            setBanner({ tone: 'red', text: `Could not update cadence: ${msg}` });
        } finally {
            setCadenceBusy(false);
        }
    };

    const saveBooleanQuery = async () => {
        setBoolBusy(true);
        setBoolError('');
        try {
            const res = await bp_put_boolean_query({ raw: boolDraft });
            const d = unwrap(res) || {};
            setBoolSaved(d);
            setBanner({
                tone: 'green',
                text: `Boolean expression saved · ${d.clause_count} clause${d.clause_count === 1 ? '' : 's'} · ${d.matches_in_last_30d} matches in last 30d · click Scan now to apply`,
            });
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'save failed';
            setBoolError(msg);
        } finally {
            setBoolBusy(false);
        }
    };

    const handleSetDraftField = (typeKey, fieldName, value) => {
        setDrafts(d => ({ ...d, [typeKey]: { ...(d[typeKey] || {}), [fieldName]: value } }));
        // Debounced preview against the *primary* field only — that's the
        // one the runtime anchor regex actually hits. Other fields are
        // metadata (role, aliases, dates) and don't affect match count.
        const typeDef = ENTRY_TYPES.find(t => t.key === typeKey);
        if (!typeDef || fieldName !== typeDef.primaryField) return;
        const phrase = String(value || '').trim();
        if (phrase.length < 3) {
            setPreviews(p => ({ ...p, [typeKey]: null }));
            return;
        }
        setPreviews(p => ({ ...p, [typeKey]: { loading: true } }));
        // Debounce — only run preview after the user pauses typing for 400ms.
        clearTimeout(handleSetDraftField._t);
        handleSetDraftField._t = setTimeout(() => {
            bp_anchor_preview({ phrase })
                .then(r => {
                    const d = unwrap(r) || {};
                    setPreviews(p => ({
                        ...p, [typeKey]: {
                            loading: false,
                            count: d.match_count || 0,
                            sample_size: d.sample_size || 0,
                            is_specific_enough: !!d.is_specific_enough,
                            hint: d.hint || '',
                            sample_matches: d.sample_matches || [],
                        }
                    }));
                })
                .catch(() => {
                    setPreviews(p => ({ ...p, [typeKey]: { loading: false, error: true } }));
                });
        }, 400);
    };

    const handleAdd = async (typeDef) => {
        const draft = drafts[typeDef.key] || {};
        // Validate required fields
        const missing = typeDef.fields.filter(f => f.required && !(draft[f.name] || '').toString().trim());
        if (missing.length) {
            setBanner({ tone: 'red', text: `Missing: ${missing.map(f => f.label).join(', ')}` });
            return;
        }
        // Build value object, applying per-field parsers (e.g. comma-split aliases)
        const value = {};
        for (const f of typeDef.fields) {
            const raw = draft[f.name];
            if (raw === undefined || raw === '' || raw === null) continue;
            if (f.type === 'checkbox') {
                value[f.name] = !!raw;
            } else if (f.parse) {
                const parsed = f.parse(String(raw));
                if (Array.isArray(parsed) ? parsed.length : parsed)
                    value[f.name] = parsed;
            } else {
                value[f.name] = String(raw).trim();
            }
        }
        // For brand_handle: normalize canonical_handle (strip any leading @) and merge
        // platforms when the same handle already exists, instead of overwriting.
        if (typeDef.key === 'brand_handle') {
            if (value.canonical_handle)
                value.canonical_handle = value.canonical_handle.replace(/^@+/, '');
        }
        if (typeDef.key === 'brand_handle' && value.platform) {
            const existingEntry = (rowsByType['brand_handle'] || []).find(
                r => (r.value?.canonical_handle || '').replace(/^@+/, '') === value.canonical_handle
            );
            if (existingEntry) {
                const existingPlatforms = Array.isArray(existingEntry.value?.platform)
                    ? existingEntry.value.platform
                    : (existingEntry.value?.platform ? [existingEntry.value.platform] : []);
                if (existingPlatforms.includes(value.platform)) {
                    setBanner({ tone: 'red', text: `Handle '@${value.canonical_handle}' is already registered on ${value.platform}.` });
                    return;
                }
                value.platform = [...existingPlatforms, value.platform];
            }
        }

        setSavingType(typeDef.key);
        setBanner(null);
        try {
            const res = await bp_create_watchlist_entry({ entry_type: typeDef.key, value });
            const d = unwrap(res) || {};
            const created = d.created;
            setBanner({
                tone: 'green',
                text: `${created ? 'Added' : 'Updated'} · ${typeDef.label.toLowerCase()} · click Scan now to apply, or wait for the next scheduled scan (every 2 hours)`,
            });
            setDrafts(d2 => ({ ...d2, [typeDef.key]: {} }));
            refresh();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'save failed';
            setBanner({ tone: 'red', text: `Could not save: ${msg}` });
        } finally {
            setSavingType('');
        }
    };

    const handleDelete = async (entryId, typeLabel) => {
        const ok = await confirm({
            title: 'Remove entry',
            message: `Remove this ${typeLabel.toLowerCase()} entry? It will stop influencing the mention filter on the next scan.`,
            confirmLabel: 'Remove',
            danger: true,
            themeColor: false,
        });
        if (!ok) return;
        try {
            await bp_delete_watchlist_entry(entryId);
            setBanner({ tone: 'green', text: 'Removed · click Scan now to apply, or wait for the next scheduled scan (every 2 hours)' });
            refresh();
        } catch (err) {
            setBanner({ tone: 'red', text: 'Could not delete (HTTP error)' });
        }
    };

    const totalAnchors = Object.values(rowsByType).reduce((a, r) => a + (r ? r.length : 0), 0);

    return (
        <div className="bm_settings_anchors">
            {confirmModal}
            {/* FORESEE AI onboarding — auto-shows on cold start (no anchors yet);
                otherwise summon it with the button to re-suggest defaults. */}
            {(totalAnchors === 0 || aiOpen) && (
                <AIOnboardingCard forceOpen={aiOpen} onApplied={() => { setAiOpen(false); refresh(); }} />
            )}
            <div style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12,
                marginBottom: 8
            }}>
                <Body as="div" variant={2} className="bm_subtitle" style={{ flex: 1 }}>
                    These seven anchor sets feed the brand-mention precision filter
                    directly. Adding the right phrases here is the single most
                    impactful lever for closing the recall gap on Reddit / news /
                    niche-forum chatter about your org. Add as many entries as you
                    need, then click <b>Scan now</b> to apply them in one pass.
                    Otherwise, scheduled scans pick up your changes every 2 hours.
                </Body>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {totalAnchors > 0 && !aiOpen && (
                        <CommonButton
                            text="✨ Suggest with FORESEE AI"
                            variant="white" className="bm_scan_btn"
                            onClick={() => setAiOpen(true)}
                            title="Let FORESEE AI propose watchlist anchors for your org"
                            style={{ whiteSpace: 'nowrap', height: 32 }}
                        />
                    )}
                    <CommonButton
                        text={rescanBusy ? 'Dispatching…' : '↻ Scan now'}
                        variant="primary" className="bm_scan_btn"
                        disable={rescanBusy}
                        onClick={triggerRescan}
                        title="Trigger a brand-mention scan right now using the current anchor set"
                        style={{ whiteSpace: 'nowrap', height: 32 }}
                    />
                </div>
            </div>

            {banner && (
                <Body as="div" variant={1} weight="medium" style={{
                    margin: '0 0 12px 0', padding: '8px 12px', borderRadius: 6,
                    background: banner.tone === 'red' ? 'rgba(220,38,38,0.10)' : 'rgba(34,197,94,0.12)',
                    color: banner.tone === 'red' ? '#b91c1c' : '#15803d',
                    border: `1px solid ${banner.tone === 'red' ? 'rgba(220,38,38,0.45)' : 'rgba(34,197,94,0.45)'}`,
                }}>
                    {banner.text}
                </Body>
            )}

            {/* Tier selector + SerpAPI usage row (2026-05-27). */}
            {/* <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 12, padding: '10px 14px', marginBottom: 12,
                background: 'rgba(248,250,252,0.7)',
                border: '1px solid rgba(148,163,184,0.30)',
                borderRadius: 8,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 12, color: '#334155', fontWeight: 600 }}>
                        Scan tier:
                    </label>
                    <select
                        value={cadenceMinutes ?? 120}
                        disabled={cadenceBusy || cadenceMinutes === null}
                        onChange={e => handleCadenceChange(e.target.value)}
                        style={{
                            padding: '4px 8px',
                            background: '#ffffff',
                            border: '1px solid rgba(148,163,184,0.45)',
                            borderRadius: 4, color: '#0f172a',
                            fontSize: 12, fontWeight: 500,
                        }}>
                        {/* <option value={240}>Starter — every 4 hours</option> */}
            {/* <option value={120}>Standard — every 2 hours (default)</option>
                        <option value={60}>Pro — every hour</option>
                        <option value={15}>Enterprise — every 15 min</option>
                        <option value={5}>Real-time — every 5 min</option>
                        <option value={0}>Manual only — scheduled scans off</option> */}
            {/* </select> */}
            {/* {cadenceBusy && (
                        <span style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                            saving…
                        </span>
                    )} */}
            {/* </div> */}
            {/* {serpUsage && (
                    <div style={{
                        fontSize: 11, color: '#475569', textAlign: 'right',
                        lineHeight: 1.4
                    }}>
                        <div>
                            <b style={{ color: '#0f172a' }}>{serpUsage.calls_today || 0}</b>
                            <span> SerpAPI calls today</span>
                            <span style={{ marginLeft: 6, color: '#94a3b8' }}>
                                (~{(serpUsage.monthly_projection || 0).toLocaleString()} / month at current rate)
                            </span>
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>
                            counter date {serpUsage.counter_date || '—'} · UTC
                        </div>
                    </div>
                )}
            </div> */}

            {/* Boolean expression — layered ON TOP of the 7-card grid below. */}
            <div className="cmn_elevated_card" style={{
                border: '1px solid rgba(148,163,184,0.30)',
                background: 'rgba(248,250,252,0.6)',
                borderRadius: 8, padding: 14, marginBottom: 14,
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', gap: 12
                }}>
                    <div>
                        <SubHeading variant={3} as="div" weight="semibold" color="#0f172a">
                            Boolean expression <Body as="span" variant={2} weight="regular" color="#64748b" style={{ fontSize: 11, marginLeft: 6 }}>· advanced</Body>
                        </SubHeading>
                        <Body as="div" variant={2} color="#475569" style={{ fontSize: 11, marginTop: 4, maxWidth: 760, lineHeight: 1.5 }}>
                            Free-form rule layered on top of the seven anchor lists below.
                            A post is accepted if EITHER the anchor gate passes OR this expression matches —
                            strictly additive, never excludes. Syntax: quoted phrases (<code>"in partnership with CEPI"</code>),
                            bare terms, joined by <code>OR</code> / <code>AND</code> / <code>NOT</code>. Newlines act as implicit ORs.
                            Lines starting with <code>#</code> are comments.
                        </Body>
                    </div>
                    {boolSaved && (
                        <Body as="span" variant={2} weight="semibold" color="#334155" style={{
                            background: 'rgba(148,163,184,0.18)',
                            padding: '2px 8px',
                            borderRadius: 999, fontSize: 11
                        }}>
                            {boolSaved.clause_count || 0} clause{(boolSaved.clause_count || 0) === 1 ? '' : 's'}
                        </Body>
                    )}
                </div>

                <textarea
                    value={boolDraft}
                    onChange={e => setBoolDraft(e.target.value)}
                    placeholder={'# example — paste multiple OR clauses, one per line:\n"in partnership with CEPI"\n"CEPI awardee" AND vaccine\n"CEPI" AND (Hatchett OR Halton) NOT "Indianapolis Colts"'}
                    rows={6}
                    style={{
                        width: '100%', marginTop: 10,
                        padding: '8px 10px',
                        background: '#ffffff',
                        border: '1px solid rgba(148,163,184,0.45)',
                        borderRadius: 4, color: '#0f172a',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: 12, lineHeight: 1.5,
                        resize: 'vertical',
                    }}
                />
                {boolError && (
                    <Body as="div" variant={2} weight="medium" color="#b91c1c" style={{
                        marginTop: 6, padding: '6px 10px', borderRadius: 4,
                        background: 'rgba(220,38,38,0.10)',
                        border: '1px solid rgba(220,38,38,0.45)',
                        fontSize: 11,
                    }}>
                        {boolError}
                    </Body>
                )}
                <div style={{
                    marginTop: 10, display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center', gap: 8
                }}>
                    <Body as="div" variant={2} color="#475569" style={{ fontSize: 11 }}>
                        {boolSaved && boolSaved.raw === boolDraft
                            ? `Saved · ${boolSaved.matches_in_last_30d} matches in last 30 days`
                            : 'Unsaved changes — click Save to commit, then Scan now to apply.'}
                    </Body>
                    <CommonButton
                        text={boolBusy ? 'Saving…' : 'Save expression'}
                        variant="primary" className="bm_scan_btn"
                        disable={boolBusy || boolDraft === (boolSaved?.raw || '')}
                        onClick={saveBooleanQuery}
                    />
                </div>
            </div>

            {loading && Object.keys(rowsByType).length === 0 ? (
                <CommonLoader label="Loading anchors…" />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {ENTRY_TYPES.map(typeDef => {
                        const rows = rowsByType[typeDef.key] || [];
                        const draft = drafts[typeDef.key] || {};
                        const isSaving = savingType === typeDef.key;
                        return (
                            <div key={typeDef.key}
                                className="cmn_elevated_card"
                                style={{
                                    border: '1px solid #e2e8f0',
                                    background: '#fff',
                                    borderRadius: 8, padding: 14,
                                }}>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'baseline', gap: 12
                                }}>
                                    <div>
                                        <SubHeading variant={3} as="div" weight="semibold" color="#0f172a">
                                            {typeDef.label}
                                        </SubHeading>
                                        <Body as="div" variant={2} className="bm_subtitle" style={{ fontSize: 11, marginTop: 4, maxWidth: 720 }}>
                                            {typeDef.hint}
                                        </Body>
                                    </div>
                                    <Pill tone="slate">{rows.length}</Pill>
                                </div>

                                {/* Existing rows */}
                                {rows.length > 0 && (
                                    <div style={{
                                        marginTop: 10, display: 'flex',
                                        flexDirection: 'column', gap: 4
                                    }}>
                                        {rows.map(r => (
                                            <Body as="div" variant={2} color="#334155" key={r.id}
                                                style={{
                                                    display: 'flex', justifyContent: 'space-between',
                                                    alignItems: 'center', gap: 12,
                                                    padding: '6px 10px',
                                                    background: '#f1f5f9',
                                                    borderRadius: 4,
                                                }}>
                                                <span style={{
                                                    flex: 1, overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                    title={typeDef.renderRow(r.value)}>
                                                    {typeDef.renderRow(r.value)}
                                                </span>
                                                <CommonButton text="Remove" variant="error"
                                                    className="bm_settings_del_btn"
                                                    onClick={() => handleDelete(r.id, typeDef.label)}
                                                />
                                            </Body>
                                        ))}
                                    </div>
                                )}

                                {/* Add-new inline form */}
                                <div style={{
                                    marginTop: 10,
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                    gap: 8, alignItems: 'end'
                                }}>
                                    {typeDef.fields.map(f => (
                                        <div key={f.name}>
                                            <Body as="label" variant={2} color="#94a3b8" style={{
                                                display: 'block', fontSize: 11,
                                                marginBottom: 4
                                            }}>
                                                {f.label}{f.required && ' *'}
                                            </Body>
                                            {f.type === 'checkbox' ? (
                                                <input type="checkbox"
                                                    checked={!!draft[f.name]}
                                                    onChange={e => handleSetDraftField(typeDef.key, f.name, e.target.checked)} />
                                            ) : f.type === 'select' ? (
                                                <CommonDropdown
                                                    ariaLabel={f.label}
                                                    value={draft[f.name] || ''}
                                                    onChange={(v) => handleSetDraftField(typeDef.key, f.name, v)}
                                                    options={[
                                                        { value: '', label: '— select —' },
                                                        ...(f.options || []).map(o => ({ value: o, label: o })),
                                                    ]}
                                                />
                                            ) : f.type === 'date' ? (
                                                <CommonDatePicker
                                                    value={draft[f.name] || ''}
                                                    onChange={(v) => handleSetDraftField(typeDef.key, f.name, v)}
                                                    placeholder={f.placeholder || 'Select date'}
                                                />
                                            ) : (
                                                <input type={f.type || 'text'}
                                                    value={draft[f.name] || ''}
                                                    placeholder={f.placeholder}
                                                    onChange={e => handleSetDraftField(typeDef.key, f.name, e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter' && !isSaving) handleAdd(typeDef); }}
                                                    style={{
                                                        width: '100%', padding: '6px 8px',
                                                        background: '#fff',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: 4, color: '#0f172a',
                                                        fontSize: 12
                                                    }} />
                                            )}
                                        </div>
                                    ))}
                                    <CommonButton
                                        text={isSaving ? 'Saving…' : '+ Add'}
                                        variant="primary" className="bm_scan_btn"
                                        disable={isSaving}
                                        onClick={() => handleAdd(typeDef)}
                                        style={{ height: 32 }}
                                    />
                                </div>

                                {/* Pre-save preview — only renders once
                                    the customer has typed something in
                                    the primary field. */}
                                {previews[typeDef.key] && (() => {
                                    const pv = previews[typeDef.key];
                                    if (pv.loading) {
                                        return (
                                            <div style={{ marginTop: 8 }}>
                                                <CommonLoader label="Checking how many recent mentions would match…" />
                                            </div>
                                        );
                                    }
                                    if (pv.error) {
                                        return (
                                            <Body as="div" variant={2} color="#fcd34d" style={{ marginTop: 8, fontSize: 11 }}>
                                                Preview unavailable (transient error).
                                            </Body>
                                        );
                                    }
                                    // Color the count by signal quality. 700-tier
                                    // text on tinted bg — readable on light theme.
                                    const tone =
                                        pv.count === 0 ? { fg: '#475569', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.5)' }
                                            : pv.count > 50 ? { fg: '#b45309', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.55)' }
                                                : { fg: '#15803d', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.5)' };
                                    return (
                                        <Body as="div" variant={2} weight="medium" style={{
                                            marginTop: 8, padding: '6px 10px', borderRadius: 4,
                                            background: tone.bg, color: tone.fg,
                                            border: `1px solid ${tone.border}`,
                                            fontSize: 11, lineHeight: 1.5
                                        }}>
                                            <div>
                                                <b>{pv.count}</b> of {pv.sample_size} recent mentions (last 30d)
                                                would match this anchor.
                                                {!pv.is_specific_enough && pv.hint && (
                                                    <span style={{ marginLeft: 6 }}>· {pv.hint}</span>
                                                )}
                                            </div>
                                            {pv.sample_matches && pv.sample_matches.length > 0 && (
                                                <details style={{ marginTop: 4 }}>
                                                    <summary style={{ cursor: 'pointer', color: '#334155' }}>
                                                        Show {Math.min(5, pv.sample_matches.length)} matching example{pv.sample_matches.length > 1 ? 's' : ''}
                                                    </summary>
                                                    <ul style={{ margin: '4px 0 0 18px', padding: 0, color: '#334155' }}>
                                                        {pv.sample_matches.map((m, i) => (
                                                            <li key={i} style={{ marginBottom: 2 }}>
                                                                <Body as="span" variant={2} weight="semibold" color="#64748b" style={{ textTransform: 'uppercase', fontSize: 9 }}>
                                                                    {m.platform}
                                                                </Body>{' '}
                                                                {(m.snippet || '').slice(0, 140)}…
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </details>
                                            )}
                                        </Body>
                                    );
                                })()}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const SECTION_COMPONENTS = {
    watchlists: WatchlistsSection,
    anchors: MentionAnchorsSection,
    privacy: PrivacySection,
    retention: RetentionSection,
    paging: PagingSection,
    logos: LogoRegistrySection,
    muted: MutedDomainsSection,
    takedowns: TakedownsSection,
    audit: AuditLogSection,
};


export default function SettingsTab() {
    const [section, setSection] = useState('watchlists');
    const SectionComp = SECTION_COMPONENTS[section] || AuditLogSection;
    return (
        <>
            {/* <div className="bm_header">
                <div>
                    <div className="bm_subtitle">Settings · admin-only</div>
                    <div className="bm_subtitle" style={{ marginTop: 4, fontSize: 12 }}>
                        Configure watchlists, paging, logo registry, and review the
                        audit log. Edits require platform-admin or org-owner role —
                        the backend returns HTTP 403 to anyone else.
                    </div>
                </div>
            </div> */}

            <div className="bm_filters" style={{ gap: 8 }}>
                {SECTIONS.map(s => (
                    <CommonButton key={s.key}
                        text={s.label}
                        variant={section === s.key ? 'primary' : 'white'}
                        className="bm_settings_section_btn"
                        onClick={() => setSection(s.key)}
                    />
                ))}
            </div>

            <div style={{ marginTop: 12 }}>
                <SectionComp />
            </div>
        </>
    );
}
